import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { supabase } from '../../../lib/supabaseClient';
import SDOAuthModal from './SDOAuthModal';

const SDO_EVENT_ID = 'a0575e69-2e97-4ba3-a130-4f69c3a5d892';

// ========== Styled Components ==========

const PageWrapper = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 0 20px 60px;
  color: #1a1a1a;
  font-family: inherit;
`;

const HeroSection = styled.div`
  text-align: center;
  padding: 48px 0 32px;
  border-bottom: 3px solid #0010ff;
  margin-bottom: 40px;
`;

const HeroLogo = styled.img`
  max-width: 320px;
  width: 100%;
  margin-bottom: 24px;
`;

const HeroTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #0010ff;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const HeroDate = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const HeroLocation = styled.div`
  font-size: 15px;
  color: #666;
  margin-bottom: 16px;
`;

const HeroTagline = styled.p`
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  font-style: italic;
  margin: 0;
`;

const SectionTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 40px 0 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #E5E7EB;
`;

const SubTitle = styled.h3`
  font-size: 17px;
  font-weight: 700;
  color: #333;
  margin: 24px 0 8px;
`;

const Text = styled.div`
  font-size: 15px;
  line-height: 1.7;
  color: #444;
  margin-bottom: 16px;
  white-space: pre-wrap;

  strong { color: #1a1a1a; }
  a { color: #0010ff; text-decoration: underline; }
  ul { padding-left: 20px; margin: 8px 0; }
  li { margin-bottom: 4px; }
`;

const TimelineSection = styled.div`
  background: #F3F4F6;
  border-radius: 12px;
  padding: 24px;
  margin: 24px 0 32px;
`;

const TimelineItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
  font-size: 14px;
  border-bottom: 1px solid #E5E7EB;
  &:last-child { border-bottom: none; }
`;

const TimelineDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${p => p.highlight ? '#0010ff' : '#9CA3AF'};
  margin-top: 5px;
  flex-shrink: 0;
`;

const TimelineDate = styled.span`
  font-weight: 600;
  min-width: 180px;
  color: #333;
`;

const TimelineLabel = styled.span`
  color: #555;
`;

const PopSection = styled.div`
  background: ${p => p.variant === 'a' ? '#EEF2FF' : '#ECFDF5'};
  border: 2px solid ${p => p.variant === 'a' ? '#C7D2FE' : '#A7F3D0'};
  border-radius: 16px;
  padding: 28px;
  margin: 24px 0;
  text-align: center;

  h3 {
    font-size: 18px;
    font-weight: 700;
    color: ${p => p.variant === 'a' ? '#3730A3' : '#065F46'};
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
    color: #555;
    margin-bottom: 16px;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const CTAButton = styled.button`
  display: inline-block;
  padding: 14px 40px;
  background: ${p => p.disabled ? '#9CA3AF' : '#0010ff'};
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 700;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #0008cc;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0,16,255,0.3);
  }
`;

const ClosedBadge = styled.div`
  display: inline-block;
  padding: 14px 40px;
  background: #9CA3AF;
  color: white;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const PopBDateHint = styled.div`
  font-size: 13px;
  color: #065F46;
  font-weight: 600;
  margin-top: 8px;
`;

const InfoBlock = styled.div`
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  padding: 24px;
  margin: 16px 0;

  h4 {
    font-size: 15px;
    font-weight: 700;
    color: #333;
    margin-bottom: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const PriceTag = styled.div`
  display: inline-block;
  padding: 8px 16px;
  background: ${p => p.free ? '#D1FAE5' : '#FEF3C7'};
  color: ${p => p.free ? '#065F46' : '#92400E'};
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  margin: 4px 0;
`;

const PartnerLogos = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 40px;
  flex-wrap: wrap;
  padding: 32px 0;
  margin-top: 40px;
  border-top: 2px solid #E5E7EB;
`;

const LogoBox = styled.div`
  width: 140px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
    filter: grayscale(30%);
    opacity: 0.8;
    transition: all 0.2s;
    &:hover { filter: none; opacity: 1; }
  }
`;

const ContactBlock = styled.div`
  background: #F9FAFB;
  border-radius: 8px;
  padding: 16px;
  margin-top: 12px;
  font-size: 14px;
  color: #555;
  a { color: #0010ff; }
`;

const ContactReasons = styled.div`
  background: #F0F4FF;
  border: 1px solid #C7D2FE;
  border-radius: 8px;
  padding: 14px 16px;
  margin-top: 12px;
  font-size: 13px;
  color: #444;
  line-height: 1.6;

  strong { color: #333; display: block; margin-bottom: 6px; }
  ul { padding-left: 0; margin: 0; list-style: none; }
  li { margin-bottom: 2px; &::before { content: '– '; } }
`;

const CompanyListSection = styled.div`
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  padding: 24px;
  margin: 24px 0;

  h4 {
    font-size: 16px;
    font-weight: 700;
    color: #333;
    margin-bottom: 4px;
  }

  .subtitle {
    font-size: 13px;
    color: #888;
    margin-bottom: 16px;
    font-style: italic;
  }
`;

const CompanyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 24px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const CompanyItem = styled.div`
  font-size: 14px;
  color: #333;
  padding: 4px 0;
  border-bottom: 1px solid #F3F4F6;
`;

// ========== Component ==========

const SDO2026LandingPage = ({ fullUser, fullUserLoading, refreshUser }) => {
  const { i18n } = useTranslation('common');
  const navigate = useNavigate();
  const fr = i18n.language === 'fr';

  const [event, setEvent] = useState(null);
  const [popACompanies, setPopACompanies] = useState([]);
  const [authModal, setAuthModal] = useState({ open: false, redirectTo: null });

  useEffect(() => {
    supabase.from('events').select('*').eq('id', SDO_EVENT_ID).single()
      .then(({ data }) => { if (data) setEvent(data); });
    // Fetch validated Pop A companies for display
    supabase.from('sde_registrations')
      .select('company_name')
      .eq('event_id', SDO_EVENT_ID)
      .eq('population_type', 'pop_a')
      .eq('status', 'validated')
      .then(({ data }) => {
        if (data) {
          const names = data
            .map(r => r.company_name)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b, 'fr'));
          setPopACompanies(names);
        }
      });
  }, []);

  const registrationsClosed = true;

  const handleCTA = (pop) => {
    const url = `/evenements/sdo/register?eid=${SDO_EVENT_ID}&pop=${pop}`;
    if (fullUser) {
      navigate(url);
    } else {
      setAuthModal({ open: true, redirectTo: url });
    }
  };

  const popBOpen = event?.pop_b_open;

  return (
    <PageWrapper>
      {/* Hero */}
      <img src="/img/sdo2026/bandeau-logo.png" alt="Speed Dating Design Objet" style={{ width: '100%', borderRadius: 12, marginBottom: 24 }} />

      <HeroSection>
        <HeroTitle>Speed Dating Design Objet</HeroTitle>
        <HeroDate>Mardi 29 septembre 2026, 14h-18h</HeroDate>
        <HeroDate style={{ fontSize: 15, fontWeight: 500, color: '#0010ff' }}>En présentiel</HeroDate>
        <HeroLocation>Le FRENCH DESIGN Galerie, 120 avenue Ledru Rollin, 75011 Paris</HeroLocation>
        <HeroTagline>Le meilleur du design à la rencontre des meilleurs savoir-faire</HeroTagline>
      </HeroSection>

      {/* Intro */}
      <Text style={{ fontSize: 20, fontWeight: 600, textAlign: 'center', color: '#1a1a1a' }}>
        <strong>Rencontres entre fabricants</strong> (industriels, éditeurs, galeries…) <strong>et designers produit</strong>
        {'\n\n'}
        Une opportunité exceptionnelle offerte aux entreprises françaises de développer de nouvelles collaborations : nouvelle collection, réédition, valorisation de chutes, nouveaux usages, image de marque ou projet inédit...
        {'\n\n'}
        <strong>Jusqu'à 8 rendez-vous en 1/2 journée</strong>
      </Text>

      <Text style={{ background: '#FEF3C7', padding: 16, borderRadius: 8, fontWeight: 600, color: '#92400E' }}>
        Merci de lire attentivement les informations suivantes !
      </Text>

      {/* Planning */}
      <SectionTitle>Planning</SectionTitle>
      <TimelineSection>
        <TimelineItem>
          <TimelineDot />
          <TimelineDate>1er juin → 4 septembre</TimelineDate>
          <TimelineLabel>Inscriptions fabricants/éditeurs (Pop A)</TimelineLabel>
        </TimelineItem>
        <TimelineItem>
          <TimelineDot />
          <TimelineDate>22 juin → 4 septembre</TimelineDate>
          <TimelineLabel>Candidatures designers (Pop B)</TimelineLabel>
        </TimelineItem>
        <TimelineItem>
          <TimelineDot />
          <TimelineDate>Début septembre</TimelineDate>
          <TimelineLabel>Envoi planning détaillé aux participants</TimelineLabel>
        </TimelineItem>
        <TimelineItem>
          <TimelineDot highlight />
          <TimelineDate>Mardi 29 septembre</TimelineDate>
          <TimelineLabel><strong>Speed Dating Design Objet</strong></TimelineLabel>
        </TimelineItem>
        <div style={{ paddingLeft: 22, marginTop: 4, fontSize: 14, color: '#444', lineHeight: 1.8 }}>
          <strong>13h00</strong> : Accueil des participants<br />
          <strong>14h-17h</strong> : Rendez-vous & pauses networking<br />
          <strong>17h30</strong> : Cocktail de clôture
        </div>
      </TimelineSection>

      {/* Pop A Section */}
      <PopSection variant="a">
        <h3>Vous êtes fabricant / éditeur, artisan, galeriste, à la tête d'une marque de mobilier ? (Pop A)</h3>
        <p>
          Vous souhaitez travailler avec des designers émergents ou confirmés, pour développer de nouvelles collaborations, de nouveaux produits ?
        </p>
        {registrationsClosed
          ? <ClosedBadge>Inscriptions closes</ClosedBadge>
          : <CTAButton onClick={() => handleCTA('a')}>Inscriptions</CTAButton>
        }
      </PopSection>

      {/* Pop A Info Pratiques */}
      <InfoBlock>
        <h4>Informations pratiques — Pop A</h4>
        <Text>
          → Soyez le plus précis possible dans la description de votre besoin ! Vous avez jusqu'au <strong>vendredi 4 septembre, minuit</strong>, pour compléter et modifier votre demande.
          {'\n\n'}
          → Après validation de votre inscription (notre équipe vous contactera pour obtenir un complément d'informations), celle-ci est mise en ligne. La communauté de designers prendra alors connaissance de vos besoins, et adressera ses demandes de rendez-vous (ou candidatures) via la plateforme.
          {'\n\n'}
          → Le FRENCH DESIGN sélectionne pour vous les profils les plus adaptés à vos besoins, parmi les candidatures reçues.
          {'\n\n'}
          → La liste de vos rendez-vous vous sera communiquée au <strong>début du mois de septembre</strong>. Jusqu'à <strong>8 rendez-vous</strong> pourront vous être proposés.
          {'\n\n'}
          → <strong>Jour J</strong> : une série de rendez-vous d'une durée de 15mn environ est proposée aux participants. Ces rendez-vous vous permettent d'établir un premier contact en vue de futures collaborations. Des pauses networking sont prévues pendant l'évènement.
          {'\n\n'}
          → Nous ne transmettons ni vos noms ni vos coordonnées jusqu'au jour J. Vous serez libres de vous organiser sur place directement avec vos interlocuteurs pour échanger vos contacts, et poursuivre les échanges avec les uns et les autres après l'événement, si vous le souhaitez.
          {'\n\n'}
          → Afin de garantir la confidentialité des informations que vous partagerez le jour J, un accord de confidentialité sera signé en amont de l'événement entre les designers et Le FRENCH DESIGN.
        </Text>
        <ContactReasons>
          <strong>Pour toute aide relative à votre inscription :</strong>
          <ul>
            <li>Projet ou besoin spécifique</li>
            <li>Questions diverses sur la collaboration avec un designer</li>
            <li>Fonctionnement général événement</li>
            <li>Problèmes liés votre inscription</li>
            <li>Etc.</li>
          </ul>
          <strong>Deux possibilités pour nous contacter :</strong>{'\n'}
          <a href="mailto:design@via.fr">design@via.fr</a> | <a href="https://meetings.hubspot.com/pointreau?uuid=2f050e34-4d0f-4a1e-a298-f9ec41ea0338" target="_blank" rel="noreferrer">Échange en visio (15mn)</a>
        </ContactReasons>
      </InfoBlock>

      {/* Pop A Modalités */}
      <InfoBlock>
        <h4>Modalités de participation — Pop A</h4>
        <SubTitle>1/ Frais de dossier</SubTitle>
        <Text>
          Vous devrez être membre de la communauté <strong>Connected by Le FRENCH DESIGN</strong> pour pouvoir participer au Speed Dating le jour J.{'\n'}
          Montant de l'adhésion : <strong>35 € TTC / an / personne</strong>.
        </Text>
        <Text>
          Plusieurs cas de figure :{'\n'}
          • Vous êtes déjà membre de la communauté ? Parfait ! Vous n'avez rien à faire.{'\n'}
          • Si votre société est membre de l'<strong>Ameublement français</strong>, cotisante <strong>CODIFAB</strong> ou adhérente à l'<strong>UNAMA</strong>, votre adhésion à la communauté est déjà incluse dans vos cotisations. Il vous suffit de cocher la case correspondante, au moment de votre inscription, pour nous le signaler et nous permettre de vérifier.{'\n'}
          • Pour tout autre cas, un lien de paiement vous sera adressé ultérieurement pour procéder au règlement de votre adhésion.
        </Text>
        <SubTitle>2/ Frais de participation</SubTitle>
        <PriceTag>100 € TTC</PriceTag>
        <Text style={{ marginTop: 8 }}>
          Si votre société est membre de l'Ameublement français, cotisante CODIFAB ou adhérente à l'UNAMA, <strong>vous n'avez rien à régler</strong> : les frais de participation sont déjà inclus dans vos cotisations. Il vous suffit de cocher la case correspondante, au moment de votre inscription, pour nous le signaler et nous permettre de vérifier.{'\n'}
          Pour tout autre cas, un lien de paiement vous sera adressé ultérieurement pour procéder au règlement.
        </Text>
      </InfoBlock>

      {/* Pop B Section */}
      <PopSection variant="b">
        <h3>Vous êtes designer / studio de design ? (Pop B)</h3>
        <p>
          Vous souhaitez proposer vos services à un fabricant/éditeur, artisan, marque de mobilier, galerie ?
        </p>
        {registrationsClosed
          ? <ClosedBadge>Candidatures closes</ClosedBadge>
          : <CTAButton onClick={() => handleCTA('b')}>Candidatures</CTAButton>
        }
      </PopSection>

      {/* Pop A companies list */}
      {popACompanies.length > 0 && (
        <CompanyListSection>
          <h4>Entreprises inscrites à ce jour</h4>
          <div className="subtitle">Cette liste est actualisée au fil des inscriptions.<br/>La liste des entreprises participantes est susceptible d'évoluer jusqu'à la tenue de l'événement, en fonction des nouvelles inscriptions et d'éventuelles annulations.</div>
          <CompanyGrid>
            {popACompanies.map((name, i) => (
              <CompanyItem key={i}>{name}</CompanyItem>
            ))}
          </CompanyGrid>
        </CompanyListSection>
      )}

      {/* Pop B Info Pratiques */}
      <InfoBlock>
        <h4>Informations pratiques — Pop B</h4>
        <Text>
          → Sur la base des besoins exprimés par les fabricants/éditeurs, que vous aurez pris le temps de consulter au préalable, vous présentez votre offre et effectuez des demandes de rendez-vous en ligne. Vous avez jusqu'au <strong>vendredi 4 septembre inclus, minuit</strong>, pour compléter et amender vos demandes de rendez-vous.
          {'\n\n'}
          → Les fabricants/éditeurs n'ont pas accès à vos dossiers de candidature. C'est Le FRENCH DESIGN qui prend connaissance de vos candidatures et opère une sélection parmi les dossiers reçus. Toutefois, à l'issue du matching, si un ou des rendez-vous ne vous conviennent pas, vous aurez la possibilité de demander une annulation (modalités communiquées ultérieurement).
          {'\n\n'}
          → La liste de vos rendez-vous vous sera communiquée au <strong>début du mois de septembre</strong>.
          {'\n\n'}
          → Nous ne transmettons ni vos noms ni vos coordonnées en amont de l'événement. Vous serez libres de vous organiser le jour J directement avec vos interlocuteurs.
          {'\n\n'}
          → <strong>Jour J</strong> : une série de rendez-vous d'une durée de 15mn environ est proposée aux participants. Ces rendez-vous vous permettent d'établir un premier contact en vue de futures collaborations. Des pauses networking sont prévues pendant l'évènement.
        </Text>
        <ContactReasons>
          <strong>Pour toute aide relative à votre candidature :</strong>
          <ul>
            <li>Projet ou besoin spécifique</li>
            <li>Questions diverses sur la collaboration avec un fabricant/éditeur</li>
            <li>Fonctionnement général événement</li>
            <li>Problèmes liés votre candidature</li>
            <li>Etc.</li>
          </ul>
          <strong>Deux possibilités pour nous contacter :</strong>{'\n'}
          <a href="mailto:design@via.fr">design@via.fr</a> | <a href="https://meetings.hubspot.com/pointreau?uuid=2f050e34-4d0f-4a1e-a298-f9ec41ea0338" target="_blank" rel="noreferrer">Échange en visio (15mn)</a>
        </ContactReasons>
      </InfoBlock>

      {/* Pop B Modalités */}
      <InfoBlock>
        <h4>Modalités de participation — Pop B</h4>
        <Text>
          Vous devrez être membre de la communauté <strong>Connected by Le FRENCH DESIGN</strong> pour pouvoir participer au Speed Dating le jour J.{'\n'}
          Montant de l'adhésion : <strong>35 € TTC / an / personne</strong>.
        </Text>
        <Text>
          Plusieurs cas de figure :{'\n'}
          • Vous êtes déjà membre de la communauté ? Parfait ! Vous n'avez rien à faire.{'\n'}
          • Pour tout autre cas, un lien de paiement vous sera adressé ultérieurement pour procéder au règlement de votre adhésion.
        </Text>
        <SubTitle>2/ Frais de participation</SubTitle>
        <Text>
          Forfait : <strong>50 €</strong>{'\n'}
          Un lien de paiement vous sera adressé ultérieurement pour procéder au règlement.
        </Text>
      </InfoBlock>

      {/* Partner Logos */}
      <div style={{ textAlign: 'center', marginTop: 40, paddingTop: 32, borderTop: '2px solid #E5E7EB' }}>
        <img src="/img/sdo2026/footer-logos.png" alt="Le FRENCH DESIGN by VIA — CODIFAB — Ameublement français — UNAMA" style={{ width: '100%', maxWidth: 900 }} />
      </div>

      {/* Auth Modal */}
      <SDOAuthModal
        isOpen={authModal.open}
        onClose={() => setAuthModal({ open: false, redirectTo: null })}
        redirectTo={authModal.redirectTo}
        refreshUser={refreshUser}
      />
    </PageWrapper>
  );
};

export default SDO2026LandingPage;
